/**
 * Canvas Edges Settings
 *
 * Settings panel for configuring edge visualization on the canvas.
 * Controls are only active when a canvas (edgeless mode) is open.
 */

import {
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { Switch } from '@affine/component/ui/switch';
import { useI18n } from '@affine/i18n';
import type { EdgelessGridStyle } from '@blocksuite/affine-block-surface';
import { useCallback, useEffect, useState } from 'react';

import type { EdgeRenderingService } from '../../../../../modules/streamlain/edge-infrastructure/rendering';
import { createPathStrategy } from '../../../../../modules/streamlain/edge-infrastructure/strategies';
import type {
  EdgePathType,
  EdgeSystemState,
  EdgeType,
} from '../../../../../modules/streamlain/edge-infrastructure/types';
import {
  loadEdgeSettings,
  saveEdgeSettings,
} from '../../../../../modules/streamlain/edge-infrastructure/utils/config-persistence';
import { infoBox, infoIcon, select } from './style.css';

/**
 * Global ref to EdgeRenderingService
 * Set by ReactiveEdgeService when canvas is active
 */
let globalEdgeRenderingServiceRef: EdgeRenderingService | null = null;

/**
 * Sets the EdgeRenderingService for the settings panel.
 * Called by EdgeSidebarSetup (which is now EdgeSettingsSetup).
 */
export function setCanvasEdgesRenderingService(
  service: EdgeRenderingService | null
): void {
  globalEdgeRenderingServiceRef = service;
}

/**
 * Gets the current EdgeRenderingService reference.
 */
export function getCanvasEdgesRenderingService(): EdgeRenderingService | null {
  return globalEdgeRenderingServiceRef;
}

/**
 * Canvas Edges Settings Component
 */
/**
 * Canvas Edges content sections (without page header).
 * Used inside EditorSettings tab.
 */
export const CanvasEdgesContent = () => {
  const t = useI18n();
  // Load persisted settings
  const loaded = loadEdgeSettings();

  const [systemState, setSystemState] = useState<EdgeSystemState>(loaded.state);
  const [pathType, setPathType] = useState<EdgePathType>(loaded.pathType);
  const [gridStyle, setGridStyle] = useState<EdgelessGridStyle>(
    loaded.gridStyle
  );
  const [isCanvasActive, setIsCanvasActive] = useState(
    () => globalEdgeRenderingServiceRef !== null
  );

  // Check for canvas availability periodically
  useEffect(() => {
    const checkCanvas = () => {
      const isActive = globalEdgeRenderingServiceRef !== null;
      setIsCanvasActive(isActive);
    };

    // Initial check
    checkCanvas();

    // Poll for changes (service might become available later)
    const interval = setInterval(checkCanvas, 500);
    return () => clearInterval(interval);
  }, []);

  // Persist settings on change
  useEffect(() => {
    saveEdgeSettings({
      state: systemState,
      pathType,
      routingType: 'direct',
      gridStyle,
    });
  }, [gridStyle, systemState, pathType]);

  // Apply settings to service on mount and when service becomes available
  useEffect(() => {
    if (!globalEdgeRenderingServiceRef) return;

    // Apply path strategy
    const strategy = createPathStrategy(pathType);
    globalEdgeRenderingServiceRef.setPathStrategy(strategy);

    // Apply system states
    globalEdgeRenderingServiceRef.setSystemEnabled(
      'automation',
      systemState.automation.enabled
    );
    globalEdgeRenderingServiceRef.setSystemEnabled(
      'relation',
      systemState.relation.enabled
    );
    globalEdgeRenderingServiceRef.setSystemOpacity('automation', 100);
    globalEdgeRenderingServiceRef.setSystemOpacity('relation', 100);
  }, [isCanvasActive, pathType, systemState]);

  // Handle path type change
  const handlePathTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newType = e.target.value as EdgePathType;
      setPathType(newType);
      if (globalEdgeRenderingServiceRef) {
        const strategy = createPathStrategy(newType);
        globalEdgeRenderingServiceRef.setPathStrategy(strategy);
      }
    },
    []
  );

  const handleGridStyleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setGridStyle(e.target.value as EdgelessGridStyle);
    },
    []
  );

  // Handle toggle
  const handleToggle = useCallback((system: EdgeType) => {
    setSystemState(prev => {
      const newState = {
        ...prev,
        [system]: {
          ...prev[system],
          enabled: !prev[system].enabled,
        },
      };
      globalEdgeRenderingServiceRef?.setSystemEnabled(
        system,
        newState[system].enabled
      );
      return newState;
    });
  }, []);

  return (
    <>
      {/* Canvas Status */}
      {!isCanvasActive && (
        <div className={infoBox}>
          <span className={infoIcon}>ℹ️</span>
          <span>{t['com.streamlain.streamlain.edge.inactive-desc']()}</span>
        </div>
      )}

      {/* Global Settings */}
      <SettingWrapper
        title={t['com.streamlain.streamlain.edge.global-settings']()}
      >
        <SettingRow
          name={t['com.streamlain.streamlain.edge.style']()}
          desc={t['com.streamlain.streamlain.edge.style-desc']()}
        >
          <select
            className={select}
            value={pathType}
            onChange={handlePathTypeChange}
          >
            <option value="bezier">
              {t['com.streamlain.streamlain.edge.curved']()}
            </option>
            <option value="rounded-elbow">
              {t['com.streamlain.streamlain.edge.rounded-elbow']()}
            </option>
            <option value="orthogonal">
              {t['com.streamlain.streamlain.edge.orthogonal']()}
            </option>
          </select>
        </SettingRow>
        <SettingRow
          name={t['com.streamlain.streamlain.grid.style']()}
          desc={t['com.streamlain.streamlain.grid.style-desc']()}
        >
          <select
            className={select}
            value={gridStyle}
            onChange={handleGridStyleChange}
          >
            <option value="dots">
              {t['com.streamlain.streamlain.grid.dots']()}
            </option>
            <option value="crosses">
              {t['com.streamlain.streamlain.grid.crosses']()}
            </option>
            <option value="lines">
              {t['com.streamlain.streamlain.grid.lines']()}
            </option>
          </select>
        </SettingRow>
      </SettingWrapper>

      {/* Automation Edges */}
      <SettingWrapper title={t['com.streamlain.streamlain.edge.automation']()}>
        <SettingRow
          name={t['com.streamlain.streamlain.edge.automation.show']()}
          desc={t['com.streamlain.streamlain.edge.automation.show-desc']()}
        >
          <Switch
            checked={systemState.automation.enabled}
            onChange={() => handleToggle('automation')}
          />
        </SettingRow>
      </SettingWrapper>

      {/* Relation Edges */}
      <SettingWrapper title={t['com.streamlain.streamlain.edge.relation']()}>
        <SettingRow
          name={t['com.streamlain.streamlain.edge.relation.show']()}
          desc={t['com.streamlain.streamlain.edge.relation.show-desc']()}
        >
          <Switch
            checked={systemState.relation.enabled}
            onChange={() => handleToggle('relation')}
          />
        </SettingRow>
      </SettingWrapper>
    </>
  );
};
